import React, { useEffect, useState } from "react";
import { Avatar, Box, Grid, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, Paper } from '@mui/material';
import { getAllSites } from "../../../selectors/AdminSelectors";
import { observer } from "mobx-react-lite";
import { withRouter } from "react-router-dom";
import { StyledTextField } from "../../common/StyledTextField";
import { ArrowBackIos, ArrowForwardIos, Settings, StorefrontOutlined } from "@mui/icons-material";
import { TooltipIconButton } from "../../common/StyledButtons";
import Colors from "../../../constants/Colors";
import StyledSubHeader from "../../common/StyledSubHeader";
import StyledPage from "../../common/StyledPage";

const PAGE_LIMIT = 10;

const AdminBusinessesPage = ({ history, location }) => {
    const [allSites, setAllSites] = useState(getAllSites() || []);
    const [filteredSites, setFilteredSites] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    const getQueryParams = () => {
        const params = new URLSearchParams(location.search);
        return {
            page: parseInt(params.get('page')) || 1,
            search: params.get('search') || ''
        };
    };

    const [currentPage, setCurrentPage] = useState(getQueryParams().page);

    // Update search term and filtered sites on mount or when location changes
    useEffect(() => {
        const { page, search } = getQueryParams();
        setSearchTerm(search);
        const filtered = allSites.filter(site =>
            site.site_details.site_name.toLowerCase().includes(search.toLowerCase())
        );
        setFilteredSites(filtered);
        setCurrentPage(page);
    }, [location.search, allSites]);

    const updateUrlParams = (newPage, newSearch) => {
        const params = new URLSearchParams(location.search);
        if (newPage) params.set('page', newPage);
        if (newSearch !== undefined) params.set('search', newSearch);
        history.push({ search: params.toString() });
    };

    const handleSearchAccounts = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        updateUrlParams(1, value); // reset to page 1 on search
    };

    const handlePageChange = (newPage) => {
        updateUrlParams(newPage, searchTerm);
    };

    const offset = (currentPage - 1) * PAGE_LIMIT;
    const paginatedSites = filteredSites.slice(offset, offset + PAGE_LIMIT);
    const totalPages = Math.ceil(filteredSites.length / PAGE_LIMIT);

    return (
        <StyledPage>
            <StyledSubHeader title="Business Accounts" />
            <Grid container spacing={2} sx={{ marginTop: '24px' }}>
                <Grid item xs={12}>
                    <h5 style={{ padding: 0, margin: 0 }}>
                        Overview ({filteredSites.length} Total)
                    </h5>
                </Grid>
                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ padding: '16px' }}>
                        <StyledTextField
                            label="Search Accounts"
                            value={searchTerm}
                            onChange={handleSearchAccounts}
                        />
                        <List style={{ flex: 1 }}>
                            {paginatedSites.map((site) => (
                                <ListItem
                                    dense
                                    button
                                    onClick={() => history.push(`/admin/sites/${site.site_id}/manage`)}
                                    sx={{ backgroundColor: '#fff', marginBottom: '5px', paddingTop: '10px', paddingBottom: '10px' }}
                                    key={site.site_id}
                                >
                                    <ListItemAvatar>
                                        {site.site_details.site_img_url ? (
                                            <Avatar alt={site.site_details.site_name} src={site.site_details.site_img_url} sx={{ boxShadow: '0px 0px 11px 0px rgba(0,0,0,0.31)' }} />
                                        ) : (
                                            <Avatar sx={{ boxShadow: '0px 0px 11px 0px rgba(0,0,0,0.31)' }}>
                                                <StorefrontOutlined />
                                            </Avatar>
                                        )}
                                    </ListItemAvatar>
                                    <ListItemText primary={site.site_details.site_name} />
                                    <ListItemSecondaryAction>
                                        <TooltipIconButton
                                            text={'Manage Business Account'}
                                            icon={<Settings sx={{ color: Colors.primaryColor, fontSize: '24px' }}
                                                onClick={() => history.push(`/admin/sites/${site.site_id}/manage`)}
                                            />}
                                        />
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                            {paginatedSites.length === 0 && (
                                <ListItem>
                                    <p>No business accounts found.</p>
                                </ListItem>
                            )}
                        </List>
                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                            <TooltipIconButton
                                icon={<ArrowBackIos />}
                                text={'Previous page'}
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            />
                            <h6 style={{ fontSize: '16px', paddingTop: '10px' }}>
                                {currentPage} / {totalPages || 1}
                            </h6>
                            <TooltipIconButton
                                icon={<ArrowForwardIos />}
                                text={'Next page'}
                                disabled={currentPage >= totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </StyledPage>
    );
};

export default withRouter(observer(AdminBusinessesPage));